import React, { useEffect, useState } from "react";
import type { AvatarKind } from "../../data/fakeUsers";
import type { ProfileRole } from "../../types/profiles";

export type ProfileFormValues = {
  displayName: string;
  role: ProfileRole;
  age?: number | null;
  avatar?: AvatarKind;
};

type ProfileFormProps = {
  initialValues: ProfileFormValues;
  onSubmit: (values: ProfileFormValues) => void;
  onCancel?: () => void;
  submitLabel?: string;
  helperText?: string | null;
  errorText?: string | null;
};

const roleOptions: { value: ProfileRole; label: string }[] = [
  { value: "parent", label: "Parent" },
  { value: "enfant", label: "Enfant" },
  { value: "enseignant", label: "Enseignant" },
  { value: "eleve", label: "Élève" },
];

const avatarOptions: { value: AvatarKind; label: string }[] = [
  { value: "initial", label: "Initiale" },
  { value: "boy", label: "Garçon" },
  { value: "girl", label: "Fille" },
  { value: "man", label: "Homme" },
  { value: "woman", label: "Femme" },
];

export function ProfileForm({
  initialValues,
  onSubmit,
  onCancel,
  submitLabel = "Enregistrer",
  helperText,
  errorText,
}: ProfileFormProps) {
  const [values, setValues] = useState<ProfileFormValues>(initialValues);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit({ ...values });
  }

  return (
    <form className="flex flex-col gap-3 text-sm" onSubmit={handleSubmit}>
      <div className="flex flex-col gap-1">
        <label className="text-xs text-slate-500 dark:text-slate-400" htmlFor="profile-name">
          Prénom / Pseudo
        </label>
        <input
          id="profile-name"
          type="text"
          className="rounded-lg border px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100"
          value={values.displayName}
          onChange={(e) => setValues((prev) => ({ ...prev, displayName: e.target.value }))}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs text-slate-500 dark:text-slate-400" htmlFor="profile-role">
          Rôle
        </label>
        <select
          id="profile-role"
          className="rounded-lg border px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100"
          value={values.role}
          onChange={(e) => setValues((prev) => ({ ...prev, role: e.target.value as ProfileRole }))}
        >
          {roleOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs text-slate-500 dark:text-slate-400" htmlFor="profile-age">
          Âge (optionnel)
        </label>
        <input
          id="profile-age"
          type="number"
          min={3}
          max={120}
          className="rounded-lg border px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100"
          value={values.age ?? ""}
          onChange={(e) => {
            const val = e.target.value;
            setValues((prev) => ({
              ...prev,
              age: val === "" ? undefined : Number(val),
            }));
          }}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs text-slate-500 dark:text-slate-400" htmlFor="profile-avatar">
          Avatar (optionnel)
        </label>
        <select
          id="profile-avatar"
          className="rounded-lg border px-3 py-2 text-sm bg-white dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100"
          value={values.avatar || "initial"}
          onChange={(e) => setValues((prev) => ({ ...prev, avatar: e.target.value as AvatarKind }))}
        >
          {avatarOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {helperText && (
        <p className="text-xs text-slate-600 dark:text-slate-400">{helperText}</p>
      )}
      {errorText && <p className="text-xs text-red-600 dark:text-red-400">{errorText}</p>}

      <div className="flex justify-end gap-2 pt-2 text-xs">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 rounded-full border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700"
          >
            Annuler
          </button>
        )}
        <button
          type="submit"
          className="px-3 py-1.5 rounded-full border border-blue-600 bg-blue-600 text-white hover:bg-blue-500"
        >
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

export default ProfileForm;
